import { useState, useEffect } from "react";
import {
  collection, query, where, getDocs, doc,
  updateDoc, getDoc, serverTimestamp
} from "firebase/firestore";
import { getAuth, sendSignInLinkToEmail } from "firebase/auth";
import { db } from "../firebase/config";

const FUNCTIONS_BASE = "https://us-central1-laneiq-ae0cd.cloudfunctions.net";

// ── Billing Tab Component ─────────────────────────────────────
function BillingTab({ storeData, currentUser, activeUsers, trialDays, showToast }) {
  const [loading, setLoading] = useState(false);

  const planStatus    = storeData?.planStatus || "trial";
  const currentPlan   = storeData?.plan       || "trial";
  const isActive      = planStatus === "active";
  const isPastDue     = planStatus === "past_due";
  const isCanceled    = planStatus === "canceled";
  const isOnTrial     = !isActive && !isCanceled;
  const hasBilling    = !!storeData?.stripeCustomerId;

  const planDetails = {
    starter: { name:"Starter", price:"$149", features:["Up to 5 advisors","VIN & plate lookup","Good/Better/Best pricing","TSB & recall alerts"] },
    pro:     { name:"Pro",     price:"$299", features:["Unlimited advisors","Customer-facing output","Margin control","Priority support"] },
    trial:   { name:"Trial",   price:"—",    features:[] },
  };
  const current = planDetails[currentPlan] || planDetails.trial;

  const handleUpgrade = async (plan) => {
    setLoading(true);
    try {
      const res  = await fetch(`${FUNCTIONS_BASE}/createCheckoutSession`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ plan, storeId: currentUser.storeId, email: currentUser.email }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showToast(data.error || "Failed to start checkout", "warn");
      }
    } catch (e) {
      console.error("handleUpgrade:", e);
      showToast("Connection error. Please try again.", "warn");
    }
    setLoading(false);
  };

  const handleManageBilling = async () => {
    setLoading(true);
    try {
      const res  = await fetch(`${FUNCTIONS_BASE}/createPortalSession`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ storeId: currentUser.storeId }),
      });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        showToast(data.error || "Failed to open billing portal", "warn");
      }
    } catch (e) {
      console.error("handleManageBilling:", e);
      showToast("Connection error. Please try again.", "warn");
    }
    setLoading(false);
  };

  return (
    <>
      <div className="page-head">
        <div><h1>BILLING & PLAN</h1><p>Manage your LaneIQ subscription</p></div>
        {hasBilling && (
          <button className="btn btn-ghost" onClick={handleManageBilling} disabled={loading}>
            {loading ? "Loading..." : "⚙️ Manage Billing & Invoices"}
          </button>
        )}
      </div>

      {/* STATUS BANNER */}
      {isOnTrial && trialDays > 7 && (
        <div className="trial-banner">
          <div style={{fontSize:20}}>🎉</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--green)"}}>Free Trial — {trialDays} days remaining</div>
            <div style={{fontSize:12,color:"var(--text-sub)",marginTop:2}}>You have full Pro access during your trial. Subscribe before it ends to keep access.</div>
          </div>
        </div>
      )}
      {isOnTrial && trialDays <= 7 && trialDays > 0 && (
        <div className="trial-banner" style={{borderColor:"rgba(245,166,35,0.5)",background:"rgba(245,166,35,0.06)"}}>
          <div style={{fontSize:20}}>⚠️</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--accent)"}}>Trial ends in {trialDays} day{trialDays!==1?"s":""} — subscribe now to keep access</div>
            <div style={{fontSize:12,color:"var(--text-sub)",marginTop:2}}>Add a payment method before your trial expires to avoid any interruption.</div>
          </div>
          <button className="btn btn-primary" style={{marginLeft:"auto",whiteSpace:"nowrap"}} onClick={() => handleUpgrade("pro")} disabled={loading}>
            Subscribe Now
          </button>
        </div>
      )}
      {isOnTrial && trialDays <= 0 && (
        <div className="trial-banner" style={{borderColor:"rgba(239,68,68,0.5)",background:"rgba(239,68,68,0.06)"}}>
          <div style={{fontSize:20}}>🔴</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--red)"}}>Trial expired — subscribe to restore full access</div>
            <div style={{fontSize:12,color:"var(--text-sub)",marginTop:2}}>Advisor lookups are currently limited. Subscribe below to restore everything.</div>
          </div>
          <button className="btn btn-primary" style={{marginLeft:"auto",whiteSpace:"nowrap",background:"var(--red)",color:"#fff"}} onClick={() => handleUpgrade("pro")} disabled={loading}>
            Subscribe Now
          </button>
        </div>
      )}
      {isPastDue && (
        <div className="trial-banner" style={{borderColor:"rgba(239,68,68,0.5)",background:"rgba(239,68,68,0.06)"}}>
          <div style={{fontSize:20}}>⚠️</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--red)"}}>Payment past due — please update your payment method</div>
            <div style={{fontSize:12,color:"var(--text-sub)",marginTop:2}}>We were unable to process your last payment. Update your card to keep access.</div>
          </div>
          <button className="btn btn-primary" style={{marginLeft:"auto",whiteSpace:"nowrap",background:"var(--red)",color:"#fff"}} onClick={handleManageBilling} disabled={loading}>
            Update Payment
          </button>
        </div>
      )}
      {isCanceled && (
        <div className="trial-banner" style={{borderColor:"rgba(239,68,68,0.5)",background:"rgba(239,68,68,0.06)"}}>
          <div style={{fontSize:20}}>🔴</div>
          <div>
            <div style={{fontSize:13,fontWeight:700,color:"var(--red)"}}>Subscription canceled — resubscribe to restore access</div>
          </div>
          <button className="btn btn-primary" style={{marginLeft:"auto",whiteSpace:"nowrap"}} onClick={() => handleUpgrade("pro")} disabled={loading}>
            Resubscribe
          </button>
        </div>
      )}

      {/* CURRENT PLAN CARD */}
      {isActive && (
        <div className="billing-card">
          <div className="plan-row">
            <div>
              <div className="plan-name">{current.name} Plan <span className="plan-current-tag">ACTIVE</span></div>
              <div style={{fontSize:13,color:"var(--text-muted)",marginTop:4}}>
                {currentPlan==="starter" ? "Up to 5 advisors · VIN & plate lookup · Good/Better/Best pricing" : "Unlimited advisors · Customer-facing output · Priority support"}
              </div>
            </div>
            <div className="plan-price">{current.price}<span>/mo</span></div>
          </div>
          <div className="billing-details">
            <div className="billing-stat"><div className="billing-stat-label">Active Users</div><div className="billing-stat-value">{activeUsers.length}</div></div>
            <div className="billing-stat"><div className="billing-stat-label">Plan</div><div className="billing-stat-value" style={{color:"var(--green)",textTransform:"capitalize"}}>{currentPlan}</div></div>
            <div className="billing-stat"><div className="billing-stat-label">Status</div><div className="billing-stat-value" style={{color:"var(--green)"}}>Active</div></div>
            <div className="billing-stat">
              <div className="billing-stat-label">Invoices & Cards</div>
              <div>
                <button className="btn btn-ghost btn-sm" onClick={handleManageBilling} disabled={loading} style={{marginTop:4}}>
                  Open Billing Portal
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PLAN OPTIONS */}
      <div className="section-bar" style={{marginBottom:14}}>
        <div className="section-title">{isActive ? "Change Plan" : "Choose a Plan"}</div>
      </div>
      <div className="plan-grid">
        {[
          { id:"starter", name:"Starter", price:"$149", features:["Up to 5 advisors","VIN & plate lookup","Good/Better/Best pricing","TSB & recall alerts","Lookup history & reports"] },
          { id:"pro",     name:"Pro",     price:"$299", features:["Unlimited advisors","Customer-facing output","Margin control","Priority support","Full reporting dashboard"] },
          { id:"enterprise", name:"Enterprise", price:"Custom", features:["Multi-store accounts","DMS bolt-on integration","Dedicated account manager","Custom reporting","SLA & uptime guarantee"] },
        ].map(plan => {
          const isCurrent = currentPlan === plan.id && isActive;
          return (
            <div key={plan.name} className={`plan-option ${isCurrent ? "current" : ""}`}>
              <div className="plan-option-name">
                {plan.name}{isCurrent && <span className="plan-current-tag">CURRENT</span>}
              </div>
              <div className="plan-option-price">
                {plan.price}{plan.price !== "Custom" && <span>/mo</span>}
              </div>
              {plan.features.map(f => <div key={f} className="plan-feature">✓ {f}</div>)}
              {plan.id === "enterprise" ? (
                <a href="mailto:bryan@laneiq.io?subject=LaneIQ Enterprise Inquiry"
                  className="btn btn-ghost btn-sm" style={{marginTop:12,width:"100%",display:"block",textAlign:"center",textDecoration:"none"}}>
                  Contact Sales
                </a>
              ) : !isCurrent ? (
                <button className="btn btn-primary btn-sm" style={{marginTop:12,width:"100%"}}
                  onClick={() => handleUpgrade(plan.id)} disabled={loading}>
                  {loading ? "Loading..." : isActive ? `Switch to ${plan.name}` : `Start with ${plan.name}`}
                </button>
              ) : null}
            </div>
          );
        })}
      </div>
    </>
  );
}

const US_STATES = [
  ["AL","Alabama"],["AK","Alaska"],["AZ","Arizona"],["AR","Arkansas"],["CA","California"],
  ["CO","Colorado"],["CT","Connecticut"],["DE","Delaware"],["FL","Florida"],["GA","Georgia"],
  ["HI","Hawaii"],["ID","Idaho"],["IL","Illinois"],["IN","Indiana"],["IA","Iowa"],
  ["KS","Kansas"],["KY","Kentucky"],["LA","Louisiana"],["ME","Maine"],["MD","Maryland"],
  ["MA","Massachusetts"],["MI","Michigan"],["MN","Minnesota"],["MS","Mississippi"],["MO","Missouri"],
  ["MT","Montana"],["NE","Nebraska"],["NV","Nevada"],["NH","New Hampshire"],["NJ","New Jersey"],
  ["NM","New Mexico"],["NY","New York"],["NC","North Carolina"],["ND","North Dakota"],["OH","Ohio"],
  ["OK","Oklahoma"],["OR","Oregon"],["PA","Pennsylvania"],["RI","Rhode Island"],["SC","South Carolina"],
  ["SD","South Dakota"],["TN","Tennessee"],["TX","Texas"],["UT","Utah"],["VT","Vermont"],
  ["VA","Virginia"],["WA","Washington"],["WV","West Virginia"],["WI","Wisconsin"],["WY","Wyoming"],
  ["DC","Washington D.C."]
];

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  :root {
    --bg: #0E1117; --surface: #161B27; --surface2: #1C2333;
    --border: #252D3D; --accent: #F5A623; --accent-dim: rgba(245,166,35,0.10);
    --blue: #3B82F6; --blue-dim: rgba(59,130,246,0.10);
    --green: #22C55E; --green-dim: rgba(34,197,94,0.10);
    --red: #EF4444; --red-dim: rgba(239,68,68,0.12);
    --purple: #A855F7; --purple-dim: rgba(168,85,247,0.10);
    --text: #F1F5F9; --text-sub: #94A3B8; --text-muted: #475569;
  }
  body { background: var(--bg); color: var(--text); font-family: 'DM Sans', sans-serif; min-height: 100vh; }
  .header { display: flex; align-items: center; justify-content: space-between; padding: 0 32px; height: 64px; background: rgba(22,27,39,0.97); backdrop-filter: blur(12px); border-bottom: 1px solid var(--border); position: sticky; top: 0; z-index: 100; }
  .logo { display: flex; align-items: center; gap: 10px; }
  .logo-icon { width: 32px; height: 32px; background: var(--accent); border-radius: 6px; display: flex; align-items: center; justify-content: center; font-family: 'Bebas Neue', sans-serif; font-size: 18px; color: #000; }
  .logo-text { font-family: 'Bebas Neue', sans-serif; font-size: 24px; letter-spacing: 3px; }
  .logo-text span { color: var(--accent); }
  .header-badge { background: var(--accent-dim); border: 1px solid rgba(245,166,35,0.3); border-radius: 20px; padding: 4px 12px; font-size: 11px; font-weight: 700; color: var(--accent); letter-spacing: 1px; text-transform: uppercase; }
  .header-right { display: flex; align-items: center; gap: 14px; }
  .header-store { font-size: 12px; color: var(--text-muted); }
  .header-store strong { color: var(--text-sub); }
  .avatar { width: 34px; height: 34px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; }
  .logout-btn { background: transparent; border: 1px solid var(--border); border-radius: 6px; padding: 6px 12px; font-size: 12px; color: var(--text-muted); cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; }
  .logout-btn:hover { border-color: var(--red); color: var(--red); }
  .layout { display: grid; grid-template-columns: 220px 1fr; min-height: calc(100vh - 64px); }
  .sidebar { background: var(--surface); border-right: 1px solid var(--border); padding: 24px 0; display: flex; flex-direction: column; gap: 4px; }
  .sidebar-label { font-size: 10px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); padding: 0 20px; margin: 16px 0 8px; }
  .nav-item { display: flex; align-items: center; gap: 10px; padding: 9px 20px; font-size: 13px; font-weight: 500; color: var(--text-sub); cursor: pointer; transition: all 0.15s; border-left: 3px solid transparent; }
  .nav-item:hover { background: var(--surface2); color: var(--text); }
  .nav-item.active { background: var(--accent-dim); color: var(--accent); border-left-color: var(--accent); font-weight: 600; }
  .nav-icon { font-size: 15px; width: 20px; text-align: center; }
  .nav-badge { margin-left: auto; background: var(--red); color: #fff; border-radius: 10px; padding: 1px 7px; font-size: 10px; font-weight: 700; }
  .main { padding: 36px; background: var(--bg); overflow-y: auto; }
  .page-head { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 28px; }
  .page-head h1 { font-family: 'Bebas Neue', sans-serif; font-size: 32px; letter-spacing: 3px; line-height: 1; }
  .page-head p { font-size: 13px; color: var(--text-muted); margin-top: 5px; }
  .stat-strip { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 28px; }
  .stat-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 18px 20px; }
  .stat-card.urgent { border-color: var(--red); background: var(--red-dim); }
  .stat-card.positive { border-color: rgba(34,197,94,0.3); }
  .stat-label { font-size: 11px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 8px; }
  .stat-value { font-family: 'Bebas Neue', sans-serif; font-size: 36px; letter-spacing: 1px; line-height: 1; }
  .stat-value.red { color: var(--red); } .stat-value.green { color: var(--green); } .stat-value.amber { color: var(--accent); }
  .stat-sub { font-size: 11px; color: var(--text-muted); margin-top: 4px; }
  .section-bar { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
  .section-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); display: flex; align-items: center; gap: 8px; }
  .section-title::before { content: ''; display: block; width: 3px; height: 12px; background: var(--accent); border-radius: 2px; }
  .btn { padding: 8px 16px; border-radius: 7px; font-size: 12px; font-weight: 700; cursor: pointer; font-family: 'DM Sans', sans-serif; transition: all 0.15s; border: none; }
  .btn-primary { background: var(--accent); color: #000; } .btn-primary:hover { opacity: 0.88; }
  .btn-ghost { background: transparent; border: 1px solid var(--border); color: var(--text-sub); } .btn-ghost:hover { border-color: var(--text-sub); color: var(--text); }
  .btn-danger { background: var(--red-dim); border: 1px solid rgba(239,68,68,0.3); color: var(--red); }
  .btn-green { background: var(--green-dim); border: 1px solid rgba(34,197,94,0.3); color: var(--green); }
  .btn-sm { padding: 5px 10px; font-size: 11px; }
  .btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .pending-list { display: flex; flex-direction: column; gap: 10px; margin-bottom: 28px; }
  .pending-card { background: var(--surface); border: 1px solid rgba(239,68,68,0.25); border-radius: 10px; padding: 16px 20px; display: flex; align-items: center; gap: 16px; }
  .pending-avatar { width: 38px; height: 38px; border-radius: 50%; background: var(--red-dim); border: 1.5px solid rgba(239,68,68,0.4); display: flex; align-items: center; justify-content: center; font-size: 13px; font-weight: 700; color: var(--red); flex-shrink: 0; }
  .pending-info { flex: 1; }
  .pending-name { font-size: 14px; font-weight: 600; }
  .pending-meta { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
  .pending-role-select { background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 6px 10px; font-size: 12px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; }
  .pending-actions { display: flex; gap: 8px; }
  .user-table { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
  .user-table-header { display: grid; grid-template-columns: 2fr 1.5fr 1fr 1fr 120px; gap: 12px; padding: 11px 20px; background: var(--surface2); border-bottom: 1px solid var(--border); }
  .th { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); }
  .user-row { display: grid; grid-template-columns: 2fr 1.5fr 1fr 1fr 120px; gap: 12px; padding: 14px 20px; border-bottom: 1px solid var(--border); align-items: center; transition: background 0.12s; }
  .user-row:last-child { border-bottom: none; }
  .user-row:hover { background: var(--surface2); }
  .user-row.inactive { opacity: 0.45; }
  .user-cell { display: flex; align-items: center; gap: 10px; }
  .u-avatar { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 700; flex-shrink: 0; }
  .user-name { font-size: 13px; font-weight: 600; }
  .td { font-size: 13px; color: var(--text-sub); }
  .role-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 20px; font-size: 11px; font-weight: 700; }
  .role-admin { background: var(--accent-dim); color: var(--accent); border: 1px solid rgba(245,166,35,0.3); }
  .role-service_manager { background: var(--purple-dim); color: var(--purple); border: 1px solid rgba(168,85,247,0.3); }
  .role-parts_manager { background: var(--blue-dim); color: var(--blue); border: 1px solid rgba(59,130,246,0.3); }
  .role-advisor { background: var(--green-dim); color: var(--green); border: 1px solid rgba(34,197,94,0.3); }
  .status-dot { width: 7px; height: 7px; border-radius: 50%; display: inline-block; margin-right: 5px; }
  .status-dot.active { background: var(--green); box-shadow: 0 0 5px rgba(34,197,94,0.5); }
  .status-dot.inactive { background: var(--text-muted); }
  .row-actions { display: flex; gap: 6px; }
  .role-select-inline { background: var(--surface2); border: 1px solid var(--border); border-radius: 6px; padding: 4px 8px; font-size: 11px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; }
  .invite-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 22px; margin-bottom: 20px; }
  .invite-title { font-size: 13px; font-weight: 700; color: var(--text); margin-bottom: 16px; }
  .invite-grid { display: grid; grid-template-columns: 1fr 1fr auto; gap: 10px; align-items: end; }
  .field { display: flex; flex-direction: column; gap: 5px; }
  .field label { font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--text-muted); }
  .field input, .field select { background: var(--bg); border: 1px solid var(--border); border-radius: 7px; padding: 9px 12px; font-size: 13px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; }
  .field input:focus, .field select:focus { border-color: var(--accent); }
  .field input::placeholder { color: var(--text-muted); }
  .settings-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .settings-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 22px; }
  .settings-card-title { font-size: 11px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: var(--text-muted); margin-bottom: 18px; display: flex; align-items: center; gap: 8px; }
  .settings-card-title::before { content: ''; display: block; width: 3px; height: 12px; background: var(--accent); border-radius: 2px; }
  .settings-field { display: flex; flex-direction: column; gap: 6px; margin-bottom: 14px; }
  .settings-label { font-size: 11px; font-weight: 600; color: var(--text-muted); }
  .settings-input { background: var(--bg); border: 1px solid var(--border); border-radius: 7px; padding: 10px 13px; font-size: 13px; color: var(--text); font-family: 'DM Sans', sans-serif; outline: none; }
  .settings-input:focus { border-color: var(--accent); }
  .settings-input.readonly { opacity: 0.5; cursor: not-allowed; }
  .settings-hint { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
  .settings-save-row { display: flex; justify-content: flex-end; margin-top: 6px; }
  .billing-card { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 22px; margin-bottom: 16px; }
  .plan-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px; }
  .plan-name { font-family: 'Bebas Neue', sans-serif; font-size: 26px; letter-spacing: 2px; color: var(--accent); }
  .plan-price { font-family: 'Bebas Neue', sans-serif; font-size: 32px; color: var(--text); }
  .plan-price span { font-size: 14px; color: var(--text-muted); font-family: 'DM Sans', sans-serif; font-weight: 400; }
  .billing-details { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 16px; }
  .billing-stat { padding: 14px; background: var(--surface2); border-radius: 8px; }
  .billing-stat-label { font-size: 11px; color: var(--text-muted); font-weight: 600; margin-bottom: 4px; }
  .billing-stat-value { font-size: 15px; font-weight: 700; }
  .trial-banner { background: var(--green-dim); border: 1px solid rgba(34,197,94,0.3); border-radius: 10px; padding: 14px 18px; display: flex; align-items: center; gap: 12px; margin-bottom: 16px; }
  .plan-grid { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 12px; }
  .plan-option { background: var(--surface2); border: 1px solid var(--border); border-radius: 10px; padding: 16px; cursor: pointer; transition: all 0.15s; }
  .plan-option:hover { border-color: var(--accent); }
  .plan-option.current { border-color: var(--accent); box-shadow: 0 0 0 1px var(--accent); }
  .plan-option-name { font-family: 'Bebas Neue', sans-serif; font-size: 18px; letter-spacing: 1.5px; margin-bottom: 4px; }
  .plan-option-price { font-size: 20px; font-weight: 700; color: var(--accent); margin-bottom: 8px; }
  .plan-option-price span { font-size: 12px; color: var(--text-muted); font-weight: 400; }
  .plan-feature { font-size: 11px; color: var(--text-sub); margin-bottom: 3px; }
  .plan-feature::before { content: '· '; color: var(--accent); }
  .plan-current-tag { background: var(--accent); color: #000; font-size: 9px; font-weight: 700; padding: 2px 7px; border-radius: 4px; margin-left: 8px; }
  .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(4px); z-index: 500; display: flex; align-items: center; justify-content: center; }
  .modal { background: var(--surface); border: 1px solid var(--border); border-radius: 14px; padding: 28px; width: 420px; }
  .modal-title { font-family: 'Bebas Neue', sans-serif; font-size: 22px; letter-spacing: 2px; margin-bottom: 8px; }
  .modal-desc { font-size: 13px; color: var(--text-sub); line-height: 1.6; margin-bottom: 20px; }
  .modal-actions { display: flex; gap: 10px; justify-content: flex-end; }
  .toast { position: fixed; bottom: 24px; left: 50%; transform: translateX(-50%); background: var(--surface); border: 1px solid var(--green); border-radius: 8px; padding: 11px 20px; font-size: 13px; font-weight: 600; color: var(--green); z-index: 999; display: flex; align-items: center; gap: 8px; animation: slideUp 0.2s ease; }
  .toast.warn { border-color: var(--red); color: var(--red); }
  .loading-state { display: flex; align-items: center; justify-content: center; gap: 12px; padding: 48px; color: var(--text-muted); font-size: 13px; }
  .spinner-sm { width: 16px; height: 16px; border: 2px solid var(--border); border-top-color: var(--accent); border-radius: 50%; animation: spin 0.6s linear infinite; }
  @keyframes spin { to { transform: rotate(360deg); } }
  @keyframes slideUp { from { opacity: 0; transform: translateX(-50%) translateY(12px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }
`;

const ROLE_LABELS = { advisor:"Service Advisor", parts_manager:"Parts Manager", service_manager:"Service Manager", admin:"Store Admin" };
const ROLE_ICONS  = { advisor:"👨‍💼", parts_manager:"🔧", service_manager:"📊", admin:"⭐" };
const ROLES = ["advisor","parts_manager","service_manager","admin"];
const AVATAR_COLORS = ["#F5A623","#3B82F6","#22C55E","#A855F7","#EF4444","#06B6D4"];

const initials = (name="") => { const p=name.trim().split(" "); return p.length>=2?p[0][0]+p[1][0]:(p[0]?.[0]||"?"); };
const avatarColor = (str) => { let h=0; for(let i=0;i<str.length;i++) h=str.charCodeAt(i)+((h<<5)-h); return AVATAR_COLORS[Math.abs(h)%AVATAR_COLORS.length]; };

function RoleBadge({role}) {
  return <span className={`role-badge role-${role}`}>{ROLE_ICONS[role]} {ROLE_LABELS[role]||role}</span>;
}

export default function AdminDashboard({ currentUser, onLogout, onNavigate }) {
  const [activeNav, setActiveNav] = useState("users");
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [storeData, setStoreData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pendingRoles, setPendingRoles] = useState({});
  const [editingRole, setEditingRole] = useState(null);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("advisor");
  const [storeSettings, setStoreSettings] = useState({name:"",phone:"",email:"",address:"",defaultState:"OH"});

  const showToast = (msg, type="success") => { setToast({msg,type}); setTimeout(()=>setToast(null),3200); };

  useEffect(() => { if (currentUser?.storeId) loadData(); }, [currentUser]);

  const loadData = async () => {
    setLoading(true);
    try {
      const sid = currentUser.storeId;
      const storeDoc = await getDoc(doc(db,"stores",sid));
      if (storeDoc.exists()) {
        const sd = storeDoc.data();
        setStoreData(sd);
        setStoreSettings({ name:sd.name||"", phone:sd.phone||"", email:sd.email||"", address:sd.address||"", defaultState:sd.defaultState||"OH" });
      }
      const snap = await getDocs(query(collection(db,"users"), where("storeId","==",sid)));
      const all = snap.docs.map(d=>({id:d.id,...d.data()}));
      setUsers(all.filter(u=>u.status!=="pending"&&u.status!=="denied"));
      const pend = all.filter(u=>u.status==="pending");
      setPending(pend);
      const defs = {}; pend.forEach(u=>{defs[u.id]=u.role||"advisor";}); setPendingRoles(defs);
    } catch(e) { console.error(e); showToast("Error loading data","warn"); }
    setLoading(false);
  };

  const approveUser = async (u) => {
    const r = pendingRoles[u.id]||"advisor";
    try {
      await updateDoc(doc(db,"users",u.id),{status:"active",role:r,approvedAt:serverTimestamp(),approvedBy:currentUser.uid});
      setPending(p=>p.filter(x=>x.id!==u.id));
      setUsers(us=>[...us,{...u,status:"active",role:r}]);
      showToast(`${u.name} approved as ${ROLE_LABELS[r]}`);
    } catch(e) { showToast("Failed to approve","warn"); }
  };

  const denyUser = async (u) => {
    try {
      await updateDoc(doc(db,"users",u.id),{status:"denied"});
      setPending(p=>p.filter(x=>x.id!==u.id));
      showToast(`${u.name}'s request denied`,"warn");
    } catch(e) { showToast("Failed","warn"); }
  };

  const changeRole = async (uid, newRole) => {
    try {
      await updateDoc(doc(db,"users",uid),{role:newRole});
      setUsers(us=>us.map(x=>x.id===uid?{...x,role:newRole}:x));
      setEditingRole(null); showToast("Role updated");
    } catch(e) { showToast("Failed","warn"); }
  };

  const toggleActive = async (u) => {
    const ns = u.status==="active"?"inactive":"active";
    try {
      await updateDoc(doc(db,"users",u.id),{status:ns});
      setUsers(us=>us.map(x=>x.id===u.id?{...x,status:ns}:x));
      showToast(`${u.name} ${ns==="active"?"reactivated":"deactivated"}`, ns==="active"?"success":"warn");
      setModal(null);
    } catch(e) { showToast("Failed","warn"); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db,"stores",currentUser.storeId),{...storeSettings, updatedAt:serverTimestamp()});
      showToast("Store settings saved");
    } catch(e) { showToast("Failed to save","warn"); }
    setSaving(false);
  };

  const trialDays = storeData?.trialEnds
    ? Math.max(0, Math.ceil((storeData.trialEnds.toDate?.()- new Date())/(1000*60*60*24)))
    : 14;
  const activeUsers = users.filter(u=>u.status==="active");
  const ini = initials(currentUser?.name||"");
  const col = avatarColor(currentUser?.uid||"x");

  return (
    <>
      <style>{styles}</style>
      <header className="header">
        <div className="logo">
          <div className="logo-icon">L</div>
          <div className="logo-text">LANE<span>IQ</span></div>
        </div>
        <span className="header-badge">⭐ Store Admin</span>
        <div className="header-right">
          <div className="header-store"><strong>{storeData?.name||"LaneIQ"}</strong> · Admin Panel</div>
          <div className="avatar" style={{background:`${col}22`,border:`1.5px solid ${col}55`,color:col}}>{ini}</div>
          <button className="logout-btn" onClick={onLogout}>Sign Out</button>
        </div>
      </header>

      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-label">Management</div>
          {[
            {id:"users",icon:"👥",label:"Team & Users",badge:pending.length||null},
            {id:"settings",icon:"⚙️",label:"Store Settings"},
            {id:"billing",icon:"💳",label:"Billing & Plan"},
          ].map(item=>(
            <div key={item.id} className={`nav-item ${activeNav===item.id?"active":""}`} onClick={()=>setActiveNav(item.id)}>
              <span className="nav-icon">{item.icon}</span>{item.label}
              {item.badge&&<span className="nav-badge">{item.badge}</span>}
            </div>
          ))}
          <div className="sidebar-label">Quick Access</div>
          {[
            {id:"advisor",icon:"🔍",label:"Advisor View"},
            {id:"reports",icon:"📈",label:"Reports"},
            {id:"parts",icon:"🔧",label:"Parts Manager"},
          ].map(item=>(
            <div key={item.id} className="nav-item" onClick={()=>onNavigate?.(item.id)}>
              <span className="nav-icon">{item.icon}</span>{item.label}
            </div>
          ))}
        </aside>

        <main className="main">

          {/* TEAM & USERS */}
          {activeNav==="users"&&(
            <>
              <div className="page-head">
                <div><h1>TEAM & USERS</h1><p>Manage your dealership's LaneIQ users, roles, and access</p></div>
                <button className="btn btn-ghost btn-sm" onClick={loadData}>↻ Refresh</button>
              </div>
              <div className="stat-strip">
                <div className={`stat-card ${pending.length>0?"urgent":""}`}>
                  <div className="stat-label">Pending Approval</div>
                  <div className={`stat-value ${pending.length>0?"red":"green"}`}>{pending.length}</div>
                  <div className="stat-sub">{pending.length>0?"Action required":"All clear"}</div>
                </div>
                <div className="stat-card positive">
                  <div className="stat-label">Active Users</div>
                  <div className="stat-value green">{activeUsers.length}</div>
                  <div className="stat-sub">of {users.length} total</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Trial Days Left</div>
                  <div className="stat-value amber">{trialDays}</div>
                  <div className="stat-sub">Free trial</div>
                </div>
                <div className="stat-card">
                  <div className="stat-label">Your Account</div>
                  <div className="stat-value" style={{fontSize:18,marginTop:8}}>Admin</div>
                  <div className="stat-sub">{currentUser?.name}</div>
                </div>
              </div>

              {loading?(
                <div className="loading-state"><div className="spinner-sm"/>Loading users from Firestore...</div>
              ):(
                <>
                  {pending.length>0&&(
                    <>
                      <div className="section-bar"><div className="section-title">Pending Approval ({pending.length})</div></div>
                      <div className="pending-list">
                        {pending.map(u=>(
                          <div key={u.id} className="pending-card">
                            <div className="pending-avatar">{initials(u.name)}</div>
                            <div className="pending-info">
                              <div className="pending-name">{u.name}</div>
                              <div className="pending-meta">{u.email} · Requested {ROLE_LABELS[u.role]||u.role}</div>
                            </div>
                            <div style={{display:"flex",flexDirection:"column",gap:4}}>
                              <div style={{fontSize:10,color:"var(--text-muted)",fontWeight:700,textTransform:"uppercase"}}>Assign Role</div>
                              <select className="pending-role-select" value={pendingRoles[u.id]||"advisor"}
                                onChange={e=>setPendingRoles(r=>({...r,[u.id]:e.target.value}))}>
                                {ROLES.filter(r=>r!=="admin").map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                              </select>
                            </div>
                            <div className="pending-actions">
                              <button className="btn btn-green btn-sm" onClick={()=>approveUser(u)}>✓ Approve</button>
                              <button className="btn btn-danger btn-sm" onClick={()=>denyUser(u)}>✕ Deny</button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="invite-card">
                    <div className="invite-title">✉️ Invite Team Member</div>
                    <div className="invite-grid">
                      <div className="field"><label>Email Address</label>
                        <input placeholder="advisor@dealership.com" value={inviteEmail} onChange={e=>setInviteEmail(e.target.value)}/>
                      </div>
                      <div className="field"><label>Assign Role</label>
                        <select value={inviteRole} onChange={e=>setInviteRole(e.target.value)}>
                          {ROLES.filter(r=>r!=="admin").map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                        </select>
                      </div>
                      <button className="btn btn-primary" style={{height:38}} disabled={saving} onClick={async ()=>{
                        if(!inviteEmail.includes("@")){showToast("Enter a valid email","warn");return;}
                        setSaving(true);
                        try {
                          const auth = getAuth();
                          const actionCodeSettings = {
                            url: `https://laneiq.io?inviteEmail=${encodeURIComponent(inviteEmail)}&storeId=${currentUser.storeId}&role=${inviteRole}&dealerCode=${storeData?.dealerCode||""}`,
                            handleCodeInApp: true,
                          };
                          await sendSignInLinkToEmail(auth, inviteEmail, actionCodeSettings);
                          showToast(`Invite sent to ${inviteEmail}`);
                          setInviteEmail(""); setInviteRole("advisor");
                        } catch(e) {
                          console.error("Invite error:", e);
                          showToast("Failed to send invite — check the email address","warn");
                        }
                        setSaving(false);
                      }}>
                        {saving ? "Sending..." : "Send Invite"}
                      </button>
                    </div>
                    <div style={{fontSize:11,color:"var(--text-muted)",marginTop:10}}>
                      They'll receive an email with a link to create their account. Their role is pre-assigned — you can change it anytime from this panel.
                    </div>
                  </div>

                  <div className="section-bar"><div className="section-title">All Users ({users.length})</div></div>
                  {users.length===0?(
                    <div style={{padding:32,textAlign:"center",color:"var(--text-muted)",fontSize:13}}>
                      No active users yet. Approve pending requests above.
                    </div>
                  ):(
                    <div className="user-table">
                      <div className="user-table-header">
                        <div className="th">User</div><div className="th">Email</div>
                        <div className="th">Role</div><div className="th">Status</div><div className="th">Actions</div>
                      </div>
                      {users.map(u=>{
                        const c=avatarColor(u.id);
                        return(
                          <div key={u.id} className={`user-row ${u.status!=="active"?"inactive":""}`}>
                            <div className="user-cell">
                              <div className="u-avatar" style={{background:`${c}22`,border:`1.5px solid ${c}55`,color:c}}>{initials(u.name)}</div>
                              <div>
                                <div className="user-name">{u.name}{u.id===currentUser?.uid&&<span style={{fontSize:10,color:"var(--accent)",marginLeft:6}}>YOU</span>}</div>
                              </div>
                            </div>
                            <div className="td" style={{fontFamily:"'JetBrains Mono',monospace",fontSize:11}}>{u.email}</div>
                            <div className="td">
                              {editingRole===u.id?(
                                <select className="role-select-inline" defaultValue={u.role}
                                  onChange={e=>changeRole(u.id,e.target.value)} onBlur={()=>setEditingRole(null)} autoFocus>
                                  {ROLES.map(r=><option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                                </select>
                              ):(
                                <span onClick={()=>u.id!==currentUser?.uid&&setEditingRole(u.id)}
                                  style={{cursor:u.id!==currentUser?.uid?"pointer":"default"}} title="Click to change role">
                                  <RoleBadge role={u.role}/>
                                </span>
                              )}
                            </div>
                            <div className="td">
                              <span className={`status-dot ${u.status}`}/>
                              {u.status.charAt(0).toUpperCase()+u.status.slice(1)}
                            </div>
                            <div className="row-actions">
                              {u.id!==currentUser?.uid?(
                                <>
                                  <button className="btn btn-ghost btn-sm" onClick={()=>setEditingRole(u.id)}>Role</button>
                                  <button className={`btn btn-sm ${u.status==="active"?"btn-danger":"btn-green"}`}
                                    onClick={()=>setModal({type:"toggleUser",user:u})}>
                                    {u.status==="active"?"Off":"On"}
                                  </button>
                                </>
                              ):(
                                <span style={{fontSize:11,color:"var(--text-muted)"}}>Owner</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {/* STORE SETTINGS */}
          {activeNav==="settings"&&(
            <>
              <div className="page-head"><div><h1>STORE SETTINGS</h1><p>Manage your dealership information</p></div></div>
              <div className="settings-grid">
                <div className="settings-card">
                  <div className="settings-card-title">Dealership Info</div>
                  {[
                    {label:"Dealership Name",key:"name",placeholder:"e.g. Toyota of Orlando"},
                    {label:"Phone Number",key:"phone",placeholder:"(555) 000-0000"},
                    {label:"Service Email",key:"email",placeholder:"service@dealership.com"},
                    {label:"Address",key:"address",placeholder:"123 Auto Blvd, City, ST"},
                  ].map(f=>(
                    <div key={f.key} className="settings-field">
                      <label className="settings-label">{f.label}</label>
                      <input className="settings-input" placeholder={f.placeholder}
                        value={storeSettings[f.key]} onChange={e=>setStoreSettings(s=>({...s,[f.key]:e.target.value}))}/>
                    </div>
                  ))}
                  <div className="settings-field">
                    <label className="settings-label">Default State (Plate Lookups)</label>
                    <select className="settings-input"
                      value={storeSettings.defaultState}
                      onChange={e=>setStoreSettings(s=>({...s,defaultState:e.target.value}))}>
                      {US_STATES.map(([code,name])=>(
                        <option key={code} value={code}>{code} — {name}</option>
                      ))}
                    </select>
                    <div className="settings-hint">Advisors will default to this state for plate lookups.</div>
                  </div>
                  <div className="settings-save-row">
                    <button className="btn btn-primary" onClick={saveSettings} disabled={saving}>
                      {saving?"Saving...":"Save Changes"}
                    </button>
                  </div>
                </div>
                <div style={{display:"flex",flexDirection:"column",gap:16}}>
                  <div className="settings-card">
                    <div className="settings-card-title">Account Identifiers</div>
                    {[
                      {label:"Dealer Code",val:storeData?.dealerCode||""},
                      {label:"Store ID",val:currentUser?.storeId||"",mono:true},
                      {label:"Plan",val:storeData?.plan||"trial"},
                    ].map(f=>(
                      <div key={f.label} className="settings-field">
                        <label className="settings-label">{f.label}</label>
                        <input className="settings-input readonly" value={f.val} readOnly
                          style={f.mono?{fontFamily:"'JetBrains Mono',monospace",fontSize:11}:{}}/>
                      </div>
                    ))}
                    <div className="settings-hint">Dealer code and Store ID cannot be changed.</div>
                  </div>
                  <div className="settings-card">
                    <div className="settings-card-title">Your Account</div>
                    {[
                      {label:"Name",val:currentUser?.name||""},
                      {label:"Email",val:currentUser?.email||""},
                      {label:"Role",val:ROLE_LABELS[currentUser?.role]||""},
                    ].map(f=>(
                      <div key={f.label} className="settings-field">
                        <label className="settings-label">{f.label}</label>
                        <input className="settings-input readonly" value={f.val} readOnly/>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* BILLING */}
          {activeNav==="billing"&&(
            <BillingTab
              storeData={storeData}
              currentUser={currentUser}
              activeUsers={activeUsers}
              trialDays={trialDays}
              showToast={showToast}
            />
          )}

        </main>
      </div>

      {modal?.type==="toggleUser"&&(
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-title">{modal.user.status==="active"?"DEACTIVATE USER":"REACTIVATE USER"}</div>
            <div className="modal-desc">
              {modal.user.status==="active"
                ?`Deactivating ${modal.user.name} will immediately revoke their access. Their data is preserved.`
                :`Reactivating ${modal.user.name} will restore their access based on their current role.`}
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={()=>setModal(null)}>Cancel</button>
              <button className={`btn ${modal.user.status==="active"?"btn-danger":"btn-green"}`} onClick={()=>toggleActive(modal.user)}>
                {modal.user.status==="active"?"Deactivate":"Reactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast&&<div className={`toast ${toast.type==="warn"?"warn":""}`}>{toast.type==="warn"?"⚠":"✓"} {toast.msg}</div>}
    </>
  );
}
